import { useEffect, useState } from 'react';

interface ScoreRingProps {
  score: number | null | undefined; // 0-100 or null
  size?: number; // px diameter, default 120
  strokeWidth?: number; // default 10
  label?: string; // optional label under score (e.g. "/100")
  showBreakdown?: boolean;
  breakdown?: { label: string; value: number }[]; // 0-100 per item
  animate?: boolean;
  className?: string;
}

function getColor(score: number): string {
  if (score >= 70) return '#10b981'; // success
  if (score >= 40) return '#f59e0b'; // warning
  return '#ef4444'; // danger
}

export function ScoreRing({
  score,
  size = 120,
  strokeWidth = 10,
  label = '/100',
  showBreakdown = false,
  breakdown,
  animate = true,
  className,
}: ScoreRingProps) {
  const target = typeof score === 'number' ? Math.max(0, Math.min(100, score)) : 0;
  const [displayScore, setDisplayScore] = useState(animate ? 0 : target);

  useEffect(() => {
    if (!animate) {
      setDisplayScore(target);
      return;
    }
    const start = performance.now();
    const duration = 900;
    const from = displayScore;
    let raf: number;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayScore(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, animate]);

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - displayScore / 100);
  const color = getColor(displayScore);
  const isNull = score == null;

  return (
    <div className={className}>
      <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgb(255 255 255 / 0.08)"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: animate ? 'stroke-dashoffset 900ms cubic-bezier(0.4, 0, 0.2, 1), stroke 400ms' : undefined }}
          />
        </svg>
        <div
          className="absolute inset-0 flex flex-col items-center justify-center"
          role="meter"
          aria-valuenow={isNull ? undefined : displayScore}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Score ATS ${isNull ? 'non calculé' : displayScore}`}
        >
          <span className="text-3xl font-black tabular-nums" style={{ color }}>
            {isNull ? '–' : displayScore}
          </span>
          <span className="text-[10px] text-white/40 mt-0.5">{label}</span>
        </div>
      </div>
      {showBreakdown && breakdown && breakdown.length > 0 && (
        <div className="mt-4 space-y-2 w-full max-w-xs">
          {breakdown.map((item) => {
            const v = Math.max(0, Math.min(100, item.value));
            const c = getColor(v);
            return (
              <div key={item.label} className="text-xs">
                <div className="flex justify-between text-white/60 mb-1">
                  <span>{item.label}</span>
                  <span className="tabular-nums">{v}</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${v}%`, backgroundColor: c }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
